import { z } from 'zod';

/**
 * Structured filter language used by the results table, saved views, exports and the AI
 * assistant. The server compiles it to parameterized SQL against a whitelist of fields —
 * user input never becomes raw SQL.
 */
export const FILTER_OPERATORS = [
  'contains',
  'not_contains',
  'eq',
  'neq',
  'starts_with',
  'ends_with',
  'empty',
  'not_empty',
  'gt',
  'gte',
  'lt',
  'lte',
  'between',
  'in',
  'not_in',
  'is_true',
  'is_false',
] as const;

export type FilterOperator = (typeof FILTER_OPERATORS)[number];

export const FilterValueSchema = z.union([
  z.string().max(500),
  z.number(),
  z.boolean(),
  z.array(z.union([z.string().max(200), z.number()])).max(200),
]);
export type FilterValue = z.infer<typeof FilterValueSchema>;

export const FilterConditionSchema = z.object({
  field: z.string().min(1).max(120),
  op: z.enum(FILTER_OPERATORS),
  value: FilterValueSchema.optional(),
  value2: z.union([z.string().max(200), z.number()]).optional(),
});
export type FilterCondition = z.infer<typeof FilterConditionSchema>;

export interface FilterGroup {
  conjunction: 'and' | 'or';
  conditions: FilterCondition[];
  groups?: FilterGroup[];
}

export const FilterGroupSchema: z.ZodType<FilterGroup> = z.lazy(() =>
  z.object({
    conjunction: z.enum(['and', 'or']),
    conditions: z.array(FilterConditionSchema).max(40),
    groups: z.array(FilterGroupSchema).max(10).optional(),
  }),
);

export const SortSchema = z.object({
  field: z.string().min(1).max(120),
  dir: z.enum(['asc', 'desc']),
});
export type SortSpec = z.infer<typeof SortSchema>;

/** Field data types understood by the filter builder. */
export type FieldKind = 'text' | 'number' | 'boolean' | 'date' | 'list' | 'enum';

export const OPERATORS_BY_KIND: Record<FieldKind, readonly FilterOperator[]> = {
  text: ['contains', 'not_contains', 'eq', 'neq', 'starts_with', 'ends_with', 'empty', 'not_empty'],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'empty', 'not_empty'],
  boolean: ['is_true', 'is_false', 'empty', 'not_empty'],
  date: ['gt', 'gte', 'lt', 'lte', 'between', 'empty', 'not_empty'],
  list: ['contains', 'not_contains', 'empty', 'not_empty'],
  enum: ['in', 'not_in', 'eq', 'neq', 'empty', 'not_empty'],
};

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  contains: 'contains',
  not_contains: 'does not contain',
  eq: 'is',
  neq: 'is not',
  starts_with: 'starts with',
  ends_with: 'ends with',
  empty: 'is empty',
  not_empty: 'is not empty',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  between: 'is between',
  in: 'is any of',
  not_in: 'is none of',
  is_true: 'is yes',
  is_false: 'is no',
};

export function operatorNeedsValue(op: FilterOperator): boolean {
  return !['empty', 'not_empty', 'is_true', 'is_false'].includes(op);
}

export function emptyFilter(): FilterGroup {
  return { conjunction: 'and', conditions: [] };
}

export function countConditions(group: FilterGroup | undefined): number {
  if (!group) return 0;
  return group.conditions.length + (group.groups ?? []).reduce((sum, child) => sum + countConditions(child), 0);
}

/** Metadata describing one filterable/sortable column, served by the API to the UI. */
export interface FieldDescriptor {
  id: string;
  label: string;
  kind: FieldKind;
  group: string;
  sortable: boolean;
  /** Options for enum fields. */
  options?: Array<{ value: string; label: string }>;
  /** Attribute key when the field is backed by an attribute. */
  attribute?: string;
  editable?: boolean;
}
